/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Collapse, Fade, Popper, useMediaQuery } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { getPillLabel } from './progress';
import { SimProgressPanel } from './SimProgressPanel';
import { SimProgressPill } from './SimProgressPill';
import { useOpenRunRecord } from './useOpenRunRecord';
import { useOutsidePointerDown } from './useOutsidePointerDown';
import { useSimulationProgress } from './useSimulationProgress';

/** Pill width animation; the env label to its left slides with it. */
const ENTER_MS = 240;

/**
 * Top-bar simulation progress: a pill that grows in once the user's runs are active, opens a
 * popover with every run's progress, and keeps the last result until the page reloads.
 */
function SimulationProgress() {
  const progress = useSimulationProgress();
  const { phase } = progress;
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const close = useCallback(() => setIsOpen(false), []);
  const { canOpen, open } = useOpenRunRecord(close);
  useOutsidePointerDown(isOpen, [anchorRef, panelRef], close);

  if (phase === 'hidden') return null;

  const label = getPillLabel(phase, {
    running: progress.runningRuns.length,
    done: progress.doneCount,
    failed: progress.failedCount,
    cancelled: progress.cancelledCount,
  });

  return (
    // Once shown, the pill stays for the session; it only animates in.
    <Collapse in appear orientation="horizontal" timeout={prefersReducedMotion ? 0 : ENTER_MS}>
      <Box
        sx={{ display: 'flex' }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && isOpen) close();
        }}
      >
        <SimProgressPill
          ref={anchorRef}
          phase={phase}
          overall={progress.overall}
          label={label}
          isOpen={isOpen}
          onClick={() => (isOpen ? close() : setIsOpen(true))}
        />
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
          sx={{ zIndex: 1500 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={prefersReducedMotion ? 0 : 200}>
              <Box ref={panelRef}>
                <SimProgressPanel
                  overall={progress.overall}
                  runningRuns={progress.runningRuns}
                  queuedRuns={progress.queuedRuns}
                  recentRuns={progress.recentRuns}
                  progressById={progress.progressById}
                  fractionById={progress.fractionById}
                  canOpen={canOpen}
                  onOpen={open}
                />
              </Box>
            </Fade>
          )}
        </Popper>
      </Box>
    </Collapse>
  );
}

export default SimulationProgress;
